import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Users, CheckCircle2, Calendar, Award, Timer, ChevronDown, ChevronUp, X } from 'lucide-react';
import ReviewForm, { StarDisplay } from './ReviewForm';

const CATEGORY_BG = {
  arte: { bg: '#F3E8FF', border: '#844E33', text: '#7C3AED', label: 'Arte' },
  manualidades: { bg: '#FEF3C7', border: '#A8752B', text: '#A8752B', label: 'Manualidades' },
  agua: { bg: '#E0F2FE', border: '#38BDF8', text: '#0369A1', label: 'Agua' },
  cocina: { bg: '#FFEDD5', border: '#FB923C', text: '#C2410C', label: 'Cocina' },
  deporte: { bg: '#DCFCE7', border: '#4ADE80', text: '#15803D', label: 'Deporte' },
  naturaleza: { bg: '#D1FAE5', border: '#34D399', text: '#065F46', label: 'Naturaleza' },
  musica: { bg: '#FCE7F3', border: '#F472B6', text: '#BE185D', label: 'Musica' },
  general: { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151', label: 'General' },
};

function StatCard({ icon: Icon, value, label, color = '#48293A' }) {
  return (
    <div className="card-manrique p-5 text-center flex flex-col items-center gap-2">
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <p className="font-heading font-bold text-2xl text-manrique-black">{value}</p>
      <p className="text-xs text-manrique-muted">{label}</p>
    </div>
  );
}

function TimelineEntry({ reservation, isLast, api, onReviewSubmitted }) {
  const cat = CATEGORY_BG[reservation.workshop_category] || CATEGORY_BG.general;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex gap-4" data-testid={`history-entry-${reservation.id}`}>
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0 mt-1.5 ring-4 ring-white"
          style={{ backgroundColor: reservation.checked_in ? '#27AE60' : cat.border }}
        />
        {!isLast && <div className="w-0.5 flex-1 bg-black/5 mt-1" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-6 ${isLast ? 'pb-0' : ''}`}>
        <div
          className="rounded-2xl border border-black/5 bg-white p-4 hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                  style={{ backgroundColor: cat.bg, color: cat.text, borderLeft: `3px solid ${cat.border}` }}
                >
                  {cat.label}
                </span>
                <h4 className="font-heading font-bold text-sm text-manrique-black">{reservation.workshop_title}</h4>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-manrique-muted">
                <span className="flex items-center gap-1">
                  <Calendar size={11} /> {reservation.workshop_date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={11} /> {reservation.workshop_time_start} - {reservation.workshop_time_end}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {reservation.checked_in ? (
                <span className="flex items-center gap-1 text-[11px] font-medium text-[#27AE60] bg-[#27AE60]/10 px-2.5 py-1 rounded-full">
                  <CheckCircle2 size={12} /> Asistio
                </span>
              ) : (
                <span className="text-[11px] font-medium text-manrique-muted bg-[#FBF8F3] px-2.5 py-1 rounded-full">
                  {reservation.status === 'confirmed' ? 'Reservada' : reservation.status}
                </span>
              )}
              {expanded ? <ChevronUp size={14} className="text-manrique-muted" /> : <ChevronDown size={14} className="text-manrique-muted" />}
            </div>
          </div>

          {expanded && (
            <div className="mt-3 pt-3 border-t border-black/5 space-y-2 animate-fade-in">
              {reservation.workshop_description && (
                <p className="text-sm text-manrique-grey">{reservation.workshop_description}</p>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Users size={13} className="text-manrique-muted" />
                <span className="text-manrique-grey">
                  Participantes: <span className="font-medium text-manrique-black">{reservation.children_names?.join(', ')}</span>
                </span>
              </div>
              {reservation.checked_in && api && (
                <ReviewForm reservation={reservation} api={api} onReviewSubmitted={onReviewSubmitted} />
              )}
              {reservation.review && !api && (
                <div className="flex items-center gap-2 mt-1">
                  <StarDisplay rating={reservation.review.rating} size={13} />
                  {reservation.review.comment && <p className="text-xs text-manrique-grey italic">"{reservation.review.comment}"</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChildProgress({ name, stats }) {
  const pct = stats.total > 0 ? Math.round((stats.checked_in / stats.total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={`child-stats-${name}`}>
      <div className="w-8 h-8 rounded-full bg-[#48293A]/10 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-[#48293A]">{name.charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-manrique-black truncate">{name}</span>
          <span className="text-xs text-manrique-muted">{stats.checked_in}/{stats.total} talleres</span>
        </div>
        <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              backgroundColor: pct >= 75 ? '#27AE60' : pct >= 40 ? '#48293A' : '#A8752B',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function FamilyHistory({ api, isAdmin = false, userId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const url = isAdmin && userId ? `/families/${userId}/history` : '/families/me/history';
      const res = await api.get(url);
      setData(res.data);
    } catch (err) { console.error('History fetch error:', err); }
    setLoading(false);
  }, [api, isAdmin, userId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-3 border-[#48293A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || data.reservations.length === 0) {
    return (
      <div className="card-manrique text-center py-16" data-testid="history-empty">
        <Calendar size={40} className="mx-auto mb-3 text-manrique-muted opacity-30" />
        <p className="font-heading font-bold text-base text-manrique-grey">Sin actividades aun</p>
        <p className="text-sm text-manrique-muted mt-1">Las reservas y actividades apareceran aqui</p>
      </div>
    );
  }

  const { summary, child_stats, reservations, family } = data;

  // Group by date
  const byDate = {};
  reservations.forEach(r => {
    const date = r.workshop_date || 'Sin fecha';
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(r);
  });
  const sortedDates = Object.keys(byDate).sort().reverse();

  return (
    <div className="space-y-8" data-testid="family-history">
      {/* Family header (admin view) */}
      {isAdmin && family && (
        <div className="card-manrique p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#48293A]/10 flex items-center justify-center">
            <Users size={22} className="text-[#48293A]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-manrique-black">{family.family_name}</h3>
            <p className="text-sm text-manrique-grey">
              {family.room_number} - {family.children?.map(c => c.name).join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-summary">
        <StatCard icon={Calendar} value={summary.total_activities} label="Actividades" color="#48293A" />
        <StatCard icon={CheckCircle2} value={summary.total_checked_in} label="Asistencias" color="#27AE60" />
        <StatCard icon={Timer} value={`${summary.total_hours}h`} label="Horas Totales" color="#A8752B" />
        <StatCard icon={Award} value={summary.unique_workshops} label="Talleres Unicos" color="#844E33" />
      </div>

      {/* Per-child progress */}
      {Object.keys(child_stats).length > 0 && (
        <div className="card-manrique p-6" data-testid="child-progress-section">
          <h3 className="font-heading font-bold text-base text-manrique-black mb-4">Progreso por Hijo/a</h3>
          <div className="space-y-4">
            {Object.entries(child_stats).map(([name, stats]) => (
              <ChildProgress key={name} name={name} stats={stats} />
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div data-testid="history-timeline">
        <h3 className="font-heading font-bold text-base text-manrique-black mb-4">Cronologia de Actividades</h3>
        {sortedDates.map(date => (
          <div key={date} className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-black/5" />
              <span className="text-xs font-medium text-manrique-muted bg-[#FBF8F3] px-3 py-1 rounded-full">
                {date}
              </span>
              <div className="h-px flex-1 bg-black/5" />
            </div>
            <div className="ml-2">
              {byDate[date].map((r, i) => (
                <TimelineEntry
                  key={r.id}
                  reservation={r}
                  isLast={i === byDate[date].length - 1}
                  api={!isAdmin ? api : null}
                  onReviewSubmitted={fetchHistory}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
